import { useEffect, useState, type RefObject } from "react";

/**
 * AirPlay button helper for the public viewer surfaces.
 *
 * Ports the M11b portion of legacy `js/player.js:initRemotePlayback` +
 * `js/live.js:initLiveRemotePlayback` — a single hook used by both
 * the game view (VOD) and the live view (LL-HLS).
 *
 * Two API surfaces in browsers:
 *
 *   - **Safari / iOS** — non-standard `video.webkitShowPlaybackTargetPicker()`
 *     to open the picker, plus the `webkitplaybacktargetavailabilitychanged`
 *     event for availability. AirPlay only fires reliably on a
 *     `<video>` element whose `src` is a direct HLS manifest — the
 *     M11b `<HlsVideo live>` config takes the native-HLS path on
 *     Safari for exactly this reason.
 *
 *   - **Chrome / Edge / Firefox (where supported)** — the standard
 *     `RemotePlayback` API (`video.remote.watchAvailability` +
 *     `video.remote.prompt`). Less reliable for AirPlay-specific
 *     routing but the fallback path when WebKit isn't there.
 *
 * Returns:
 *   - `available`: whether the browser surfaced ANY playback target.
 *     Caller uses this to hide the button when no target is found.
 *   - `casting`: whether the video is currently routed to a remote
 *     target. Used to swap the button's icon-state class.
 *   - `prompt()`: opens the picker. No-op when no API is available.
 *
 * The hook attaches listeners to the video element when the ref
 * resolves and tears them down on unmount or ref change. Safe to
 * call before the video mounts — `available` stays `false` until
 * the element is ready.
 */

type WebkitVideo = HTMLVideoElement & {
  webkitShowPlaybackTargetPicker?: () => void;
  webkitCurrentPlaybackTargetIsWireless?: boolean;
};

type WebkitAvailabilityEvent = Event & {
  availability?: "available" | "not-available";
};

type AirPlayOptions = {
  /**
   * Optional source to hand to WebKit AirPlay before opening the picker.
   *
   * VOD HLS plays locally on iOS Safari, but some TVs reject the multivariant
   * HLS master handoff. Prefer rendering WebKit with an AirPlay-safe source
   * before the picker opens; this click-time fallback exists for callers that
   * cannot own the rendered video source.
   */
  preferredSourceUrl?: string;
};

export function useAirPlay(
  videoRef: RefObject<HTMLVideoElement | null>,
  options: AirPlayOptions = {},
): {
  available: boolean;
  casting: boolean;
  prompt: () => void;
} {
  const [available, setAvailable] = useState(false);
  const [casting, setCasting] = useState(false);
  const preferredSourceUrl = options.preferredSourceUrl;

  useEffect(() => {
    const video = videoRef.current as WebkitVideo | null;
    if (!video) return;
    // Don't disable remote playback — by default <video> exposes the
    // AirPlay button via the native controls when supported, but the
    // explicit setting is `disableRemotePlayback = false` to make
    // intent clear.
    video.disableRemotePlayback = false;

    let cleanup: (() => void) | null = null;

    if (typeof video.webkitShowPlaybackTargetPicker === "function") {
      // Safari / iOS path. Show the button by default; the
      // availability event will hide it if no targets are found.
      setAvailable(true);
      const onAvailability = (event: Event) => {
        const e = event as WebkitAvailabilityEvent;
        setAvailable(e.availability === "available");
      };
      const onWirelessChange = () => {
        setCasting(!!video.webkitCurrentPlaybackTargetIsWireless);
      };
      video.addEventListener(
        "webkitplaybacktargetavailabilitychanged",
        onAvailability,
      );
      video.addEventListener(
        "webkitcurrentplaybacktargetiswirelesschanged",
        onWirelessChange,
      );
      cleanup = () => {
        video.removeEventListener(
          "webkitplaybacktargetavailabilitychanged",
          onAvailability,
        );
        video.removeEventListener(
          "webkitcurrentplaybacktargetiswirelesschanged",
          onWirelessChange,
        );
      };
    } else if (video.remote && typeof video.remote.watchAvailability === "function") {
      // Standard RemotePlayback API — works on Chrome/Edge for some
      // remote targets (not always AirPlay, but the closest the
      // standard gets). `watchAvailability` resolves asynchronously
      // with the callback id we need to cancel on cleanup.
      //
      // **Race guard** (PR #22 review P3): if the component unmounts
      // BEFORE the Promise resolves, the cleanup runs while `cbId`
      // is still null and the browser's availability watcher is
      // never cancelled — the callback can also keep firing
      // `setAvailable` after the hook is gone (React warns on
      // setState-after-unmount). The `cancelled` flag captures the
      // post-cleanup state; if cleanup ran first, the `.then`
      // immediately cancels the watcher the moment it gets the id.
      let cbId: number | null = null;
      let cancelled = false;
      video.remote
        .watchAvailability((isAvailable: boolean) => {
          if (cancelled) return;
          setAvailable(!!isAvailable);
        })
        .then((id: number) => {
          if (cancelled) {
            // Cleanup already ran — cancel the watcher we just
            // registered, otherwise it leaks for the page lifetime.
            try {
              void video.remote?.cancelWatchAvailability(id);
            } catch {
              /* ignore */
            }
            return;
          }
          cbId = id;
        })
        .catch(() => {
          if (cancelled) return;
          setAvailable(false);
        });
      cleanup = () => {
        cancelled = true;
        if (cbId != null && video.remote) {
          try {
            void video.remote.cancelWatchAvailability(cbId);
          } catch {
            /* ignore */
          }
        }
      };
    } else {
      // No remote-playback API on this browser. Button stays hidden.
      setAvailable(false);
    }

    return () => {
      if (cleanup) cleanup();
      setCasting(false);
    };
  }, [videoRef]);

  const prompt = () => {
    const video = videoRef.current as WebkitVideo | null;
    if (!video) return;
    if (typeof video.webkitShowPlaybackTargetPicker === "function") {
      if (preferredSourceUrl) {
        const preferredUrl = new URL(
          preferredSourceUrl,
          window.location.href,
        ).href;
        if (video.currentSrc !== preferredUrl && video.src !== preferredUrl) {
          const currentTime = Number.isFinite(video.currentTime)
            ? video.currentTime
            : 0;
          const wasPlaying = !video.paused;
          const restorePosition = () => {
            if (currentTime > 0) {
              try {
                video.currentTime = currentTime;
              } catch {
                /* metadata may still be settling; AirPlay can continue at 0 */
              }
            }
            if (wasPlaying) {
              void video.play?.().catch(() => {
                /* playback may be routed remotely or require user gesture */
              });
            }
          };
          video.addEventListener("loadedmetadata", restorePosition, {
            once: true,
          });
          video.src = preferredUrl;
          video.load();
        }
      }
      try {
        video.webkitShowPlaybackTargetPicker();
      } catch {
        /* user cancelled or no target */
      }
      return;
    }
    if (video.remote && typeof video.remote.prompt === "function") {
      // remote.prompt() returns a Promise that rejects on user-cancel;
      // we silently swallow because rejecting on cancel is the spec
      // behavior, not an error to surface to the user.
      video.remote.prompt().catch(() => {
        /* user cancelled */
      });
    }
  };

  return { available, casting, prompt };
}
